import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Search, Mail, MailOpen, Archive, Trash2, Clock, CheckCircle, Reply } from "lucide-react";

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  status: "new" | "read" | "replied" | "archived";
  repliedAt?: string;
  createdAt: string;
}

const statusLabels: Record<string, string> = {
  new: "Nová",
  read: "Přečtená",
  replied: "Odpovězená",
  archived: "Archivovaná",
};

const statusColors: Record<string, string> = {
  new: "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400",
  read: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  replied: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  archived: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500",
};

export default function ContactMessagesPage() {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "new" | "read" | "replied" | "archived">("all");
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-contact-messages"],
    queryFn: () => api.get<ApiResponse<ContactMessage[]>>("/api/admin/contact-messages"),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/api/admin/contact-messages/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contact-messages"] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/contact-messages/${id}/mark-read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contact-messages"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/contact-messages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contact-messages"] });
      setSelectedMessage(null);
    },
  });

  const messages = data?.data || [];

  const filteredMessages = messages.filter((msg) => {
    const matchesSearch =
      msg.name.toLowerCase().includes(search.toLowerCase()) ||
      msg.email.toLowerCase().includes(search.toLowerCase()) ||
      msg.message.toLowerCase().includes(search.toLowerCase()) ||
      msg.subject?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = filterStatus === "all" || msg.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const newCount = messages.filter((m) => m.status === "new").length;

  const handleSelectMessage = (msg: ContactMessage) => {
    setSelectedMessage(msg);
    if (msg.status === "new") {
      markReadMutation.mutate(msg.id);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Opravdu chcete smazat tuto zprávu?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleReply = (email: string, subject?: string) => {
    const mailtoSubject = subject ? `Re: ${subject}` : "Re: Dotaz z webu";
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(mailtoSubject)}`;
    if (selectedMessage) {
      updateStatusMutation.mutate({ id: selectedMessage.id, status: "replied" });
    }
  };

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání zpráv. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Kontaktní zprávy
            {newCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary-500 px-2.5 py-0.5 text-sm font-medium text-white">
                {newCount}
              </span>
            )}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Zprávy z kontaktního formuláře
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat zprávy..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterStatus("all")}
              className={`btn-sm ${filterStatus === "all" ? "btn-primary" : "btn-ghost"}`}
            >
              Všechny
            </button>
            <button
              onClick={() => setFilterStatus("new")}
              className={`btn-sm ${filterStatus === "new" ? "btn-primary" : "btn-ghost"}`}
            >
              <Mail className="h-4 w-4" />
              Nové ({newCount})
            </button>
            <button
              onClick={() => setFilterStatus("read")}
              className={`btn-sm ${filterStatus === "read" ? "btn-primary" : "btn-ghost"}`}
            >
              <MailOpen className="h-4 w-4" />
              Přečtené
            </button>
            <button
              onClick={() => setFilterStatus("replied")}
              className={`btn-sm ${filterStatus === "replied" ? "btn-primary" : "btn-ghost"}`}
            >
              <CheckCircle className="h-4 w-4" />
              Odpovězené
            </button>
            <button
              onClick={() => setFilterStatus("archived")}
              className={`btn-sm ${filterStatus === "archived" ? "btn-primary" : "btn-ghost"}`}
            >
              <Archive className="h-4 w-4" />
              Archiv
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <Mail className="mb-2 h-12 w-12 text-gray-300" />
          <p>Žádné zprávy nenalezeny</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Messages list */}
          <div className="space-y-2">
            {filteredMessages.map((msg) => (
              <div
                key={msg.id}
                onClick={() => handleSelectMessage(msg)}
                className={`card cursor-pointer p-4 transition-all hover:shadow-md ${
                  selectedMessage?.id === msg.id
                    ? "ring-2 ring-primary-500"
                    : ""
                } ${msg.status === "new" ? "border-l-4 border-l-primary-500" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold ${msg.status === "new" ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400"}`}>
                        {msg.name}
                      </span>
                      <span className={`badge text-xs ${statusColors[msg.status]}`}>
                        {statusLabels[msg.status]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{msg.email}</p>
                    {msg.subject && (
                      <p className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {msg.subject}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {msg.message}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                      <Clock className="h-3 w-3" />
                      {new Date(msg.createdAt).toLocaleString("cs-CZ")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Message detail */}
          {selectedMessage ? (
            <div className="card sticky top-6">
              <div className="flex items-start justify-between border-b border-gray-200 pb-4 dark:border-gray-700">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedMessage.name}
                  </h3>
                  <a
                    href={`mailto:${selectedMessage.email}`}
                    className="text-primary-600 hover:underline"
                  >
                    {selectedMessage.email}
                  </a>
                  {selectedMessage.phone && (
                    <p className="text-sm text-gray-500">
                      Tel: <a href={`tel:${selectedMessage.phone}`} className="hover:underline">{selectedMessage.phone}</a>
                    </p>
                  )}
                </div>
                <span className={`badge ${statusColors[selectedMessage.status]}`}>
                  {statusLabels[selectedMessage.status]}
                </span>
              </div>

              {selectedMessage.subject && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-500">Předmět:</p>
                  <p className="text-gray-900 dark:text-white">{selectedMessage.subject}</p>
                </div>
              )}

              <div className="mt-4">
                <p className="text-sm font-medium text-gray-500">Zpráva:</p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900 dark:text-white">
                  {selectedMessage.message}
                </p>
              </div>

              <div className="mt-4 text-sm text-gray-500">
                Odesláno: {new Date(selectedMessage.createdAt).toLocaleString("cs-CZ")}
                {selectedMessage.repliedAt && (
                  <span className="ml-4">
                    Odpovězeno: {new Date(selectedMessage.repliedAt).toLocaleString("cs-CZ")}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                <button
                  onClick={() => handleReply(selectedMessage.email, selectedMessage.subject)}
                  className="btn-primary"
                >
                  <Reply className="h-4 w-4" />
                  Odpovědět
                </button>
                {selectedMessage.status !== "archived" && (
                  <button
                    onClick={() => updateStatusMutation.mutate({ id: selectedMessage.id, status: "archived" })}
                    className="btn-ghost"
                  >
                    <Archive className="h-4 w-4" />
                    Archivovat
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="btn-ghost text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                  Smazat
                </button>
              </div>
            </div>
          ) : (
            <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
              <MailOpen className="mb-2 h-12 w-12 text-gray-300" />
              <p>Vyberte zprávu pro zobrazení detailu</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
